import { useApolloClient } from "@apollo/client";
import { ModalOpenButton } from "@msys/ui";
import { BugReport as BugReportIcon } from "@mui/icons-material";
import { IconButton, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useFeature } from "../../../../common/FeatureFlags.js";
import { THREED_PLANNER_SHOW } from "../../../../environment.js";
import { useUserData } from "../../../auth/useUserData.js";
import {
  LayoutView,
  ToggleLayoutViewButton,
  ToggleLayoutViewButtonGroup,
} from "../../../commons/button/ToggleLayoutViewButtonGroup.js";
import {
  PageSubHeader,
  PageSubHeaderInjectedProps,
} from "../../../commons/layout/PageSubHeader.js";
import { useHeaderBox_ModifyItemInfoMutation } from "../../doc-items/boxes/HeaderBox.generated.js";
import { rootItemHasItemTableConstraints } from "../../doc-items/constraints.js";
import { TemplateDebugModal } from "./debug/TemplateDebugModal.js";
import { TemplateQuoteStatusChip } from "./TemplateQuoteStatus.js";
import { useTemplateQuoteStatuses } from "./TemplateQuoteStatuses.js";
import { useTemplateQuoteWarnings } from "./TemplateQuoteWarnings.js";
import { TemplatesQuoteTemplateLatestMenu } from "./TemplatesQuoteTemplateLatestMenu.js";
import { TemplatesQuoteTemplateStoreMenu } from "./TemplatesQuoteTemplateStoreMenu.js";
import {
  TemplatesQuoteTemplateSubHeader_QuoteTemplateFragment,
  TemplatesQuoteTemplateSubHeader_QuoteTemplateItemFragment,
} from "./TemplatesQuoteTemplateSubHeader.generated.js";
import { TemplatesQuoteTemplateVersionMenu } from "./TemplatesQuoteTemplateVersionMenu.js";

interface Props {
  activeView: LayoutView | null;
  docId: string;
  doc: TemplatesQuoteTemplateSubHeader_QuoteTemplateFragment;
  rootItem: TemplatesQuoteTemplateSubHeader_QuoteTemplateItemFragment;
  rootItemId: string;
  pathToDocList: string;
  pathToDoc: string;
  pathToDocVersion: string;
  onDuplicateRefetchQueries?: string[];
  onUpdateDataRefetchQueries?: string[];
  canEdit?: boolean;
  SettingsModalButton: React.ReactElement;
  ToggleApplyOverridesButton: React.ReactElement | null;
}

export const TemplatesQuoteTemplateSubHeader = ({
  activeView,
  docId,
  doc,
  rootItemId,
  rootItem,
  pathToDocList,
  pathToDoc,
  pathToDocVersion,
  onDuplicateRefetchQueries,
  onUpdateDataRefetchQueries,
  isHeaderVisible,
  setHeaderVisible,
  canEdit = false,
  SettingsModalButton,
  ToggleApplyOverridesButton,
}: Props & PageSubHeaderInjectedProps) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(["Global", "Templates", "Quote"]);
  const isTemplateDebugEnabled = useFeature("TemplateDebug");
  const viewer = useUserData().currentUser!;

  const isOwnTemplate =
    doc.owningSystemOrganisationId === viewer.organisation.id;

  const { getTemplateQuoteWarningIcon } = useTemplateQuoteWarnings();
  const { getTemplateQuoteStatusIcons } = useTemplateQuoteStatuses();

  const client = useApolloClient();
  const [modifyItemInfo] = useHeaderBox_ModifyItemInfoMutation({
    client,
    refetchQueries: onUpdateDataRefetchQueries,
    awaitRefetchQueries: true,
  });
  const handleTitleChange = async (title: string) => {
    await modifyItemInfo({
      variables: {
        input: {
          itemId: rootItemId,
          docId,
          projectId: null,
          values: {
            title,
          },
        },
      },
    });
  };

  const statusIcons: React.ReactNode[] = [
    ...getTemplateQuoteStatusIcons(doc),
    getTemplateQuoteWarningIcon(pathToDoc, doc),
  ].filter(Boolean);

  return (
    <PageSubHeader
      title={doc.title}
      status={<TemplateQuoteStatusChip doc={doc} />}
      headerType="template"
      handleTitleChange={
        canEdit
          ? async newValue => {
              await handleTitleChange(newValue);
              enqueueSnackbar(
                t("Title updated", {
                  ns: "Global",
                })
              );
            }
          : undefined
      }
      titleEditTooltip={
        canEdit
          ? t("Rename", {
              ns: "Global",
            })
          : undefined
      }
      actionButtons={
        canEdit ? (
          <TemplatesQuoteTemplateLatestMenu
            doc={doc}
            pathToDoc={pathToDoc}
            pathToDocList={pathToDocList}
            pathToDocVersion={pathToDocVersion}
            onDuplicateRefetchQueries={onDuplicateRefetchQueries}
            onUpdateDataRefetchQueries={onUpdateDataRefetchQueries}
          />
        ) : isOwnTemplate ? (
          <TemplatesQuoteTemplateVersionMenu
            doc={doc}
            pathToDoc={pathToDoc}
            pathToDocList={pathToDocList}
            pathToDocVersion={pathToDocVersion}
            onDuplicateRefetchQueries={onDuplicateRefetchQueries}
            onUpdateDataRefetchQueries={onUpdateDataRefetchQueries}
          />
        ) : (
          <TemplatesQuoteTemplateStoreMenu
            doc={doc}
            pathToDoc={pathToDoc}
            pathToDocList={pathToDocList}
            pathToDocVersion={pathToDocVersion}
          />
        )
      }
      leftButtons={
        <Stack
          direction={"row"}
          spacing={1}
          sx={{
            alignItems: "center",
          }}
        >
          {statusIcons}
          {isTemplateDebugEnabled && isOwnTemplate && (
            <ModalOpenButton
              key={"debug-template"}
              Modal={TemplateDebugModal}
              modalProps={{ docId: doc.id, pathToDocList }}
            >
              <IconButton color="primary">
                <BugReportIcon />
              </IconButton>
            </ModalOpenButton>
          )}
        </Stack>
      }
      rightButtons={
        <Stack
          direction={"row"}
          spacing={1}
          sx={{
            alignItems: "center",
          }}
        >
          {ToggleApplyOverridesButton}
          <ToggleLayoutViewButtonGroup
            activeView={activeView}
            setActiveView={view =>
              navigate(
                view === "tree"
                  ? pathToDocVersion
                  : `${pathToDocVersion}/${view}`
              )
            }
          >
            <ToggleLayoutViewButton viewType="tree" />
            {!isOwnTemplate && doc.customizable && (
              <ToggleLayoutViewButton viewType="properties" />
            )}
            {THREED_PLANNER_SHOW === "true" &&
              rootItemHasItemTableConstraints.includes(rootItem.type) &&
              (canEdit || doc.has3dRoomSnapshot) && (
                <ToggleLayoutViewButton viewType="3d" />
              )}
            {canEdit && <ToggleLayoutViewButton viewType="properties" />}
            {canEdit && <ToggleLayoutViewButton viewType="grid" />}
          </ToggleLayoutViewButtonGroup>
          {SettingsModalButton}
        </Stack>
      }
      isHeaderVisible={isHeaderVisible}
      setHeaderVisible={setHeaderVisible}
    />
  );
};
